<template>
    <DialogBox :visible="visible" header="Upload RM Data" dialogWidth="40rem" @handleClose="handleClose">
        <template #dialogContent>
            <div class="upload-card">
                <div class="upload-card-header">
                    <h3>Upload File</h3>
                </div>

                <div class="upload-rules-section">
                    <button type="button" class="rules-toggle-btn" @click="showRules = !showRules">
                        <i class="pi" :class="showRules ? config.toggle.iconUp : config.toggle.iconDown"></i>
                        <span>{{ config.toggle.title }}</span>
                    </button>
                    <div v-show="showRules" class="rules-content">
                        <div v-for="section in config.sections" :key="section.title">
                            <div v-if="section.type === 'rules'" class="rules-section">
                                <h4 class="rules-title">{{ section.title }}</h4>
                                
                                <div v-for="item in section.items" :key="item.heading" class="rule-item">
                                    <strong>{{ item.heading }}</strong>
                                    <ul>
                                        <li v-for="point in item.points" :key="point" v-html="stripHtml(point)"></li>
                                    </ul>
                                </div>
                            </div>

                            <div v-if="section.type === 'mandatory'" class="mandatory-section">
                                <h4 class="mandatory-title">{{ section.title }}</h4>
                                <p class="mandatory-intro">{{ section.intro }}</p>
                                <ul class="mandatory-list">
                                    <li v-for="col in section.columns" :key="col"><strong>{{ col }}</strong></li>
                                </ul>
                                <p class="mandatory-warning">{{ section.warning }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <FileUpload ref="uploader" name="rmCsv" accept=".xlsx" :maxFileSize="5000000" :customUpload="true"
                    chooseLabel="Select File" uploadLabel="Upload" cancelLabel="Clear" 
                    :disabled="uploading"
                    :multiple="false" :auto="false" @upload="handleUpload" @uploader="handleUpload"
                    @select="onFileSelect" @clear="onFileClear">
                    <template #empty>
                        <div class="upload-empty">
                            <i class="pi pi-cloud-upload icon" />
                            <p>Drag and drop your Excel file (.xlsx) here or click the button above.</p>
                            <small>Maximum file size 5MB. Allowed format: .xlsx only</small>
                        </div>
                    </template>
                </FileUpload>

                <div class="upload-status">
                    <p v-if="status" class="success-text">{{ status }}</p>
                    <p v-if="errorDisplayMessage" class="error-text">{{ errorDisplayMessage }}</p>
                    <Button
                        v-if="invalidRecordsFile"
                        label="Download Error Records"
                        icon="pi pi-download"
                        class="download-error-btn"
                        @click="downloadErrorRecords"
                    />
                </div>
            </div>
        </template>
    </DialogBox>
</template>
<script>
import DialogBox from '@/components/common/DialogBox.vue';
import FileUpload from 'primevue/fileupload';
import { Button } from 'primevue';
import { stripHtml } from '@/utils/inputSanitizer';

export default {
    name: 'RMUploadDialog',
    components: {
        DialogBox,
        FileUpload,
        Button
    },
    props: {
        visible: {
            type: Boolean,
            required: true
        }
    },
    emits: ['close'],
    data() {
        return {
            status: '',
            errorDisplayMessage: '',
            uploading: false,
            selectedFile: null,
            showRules: false,
            invalidRecordsFile: '',
            uploadedFileName: '',
        };
    },
    computed: {
        config() {
            const configData = this.$store.state.configData;
            if (configData && configData.RM_UI_CONFIG_TEXT) {
                return configData.RM_UI_CONFIG_TEXT;
            }
            // Fallback to default config if not available
            return {
                toggle: {
                    iconUp: "pi-chevron-up",
                    iconDown: "pi-chevron-down",
                    title: "📌 Upload Rules & Guidelines"
                },
                sections: []
            };
        }
    },
    watch: {
        visible(val) {
            if (!val) {
                this.resetState();
            }
        }
    },
    methods: {
        stripHtml,
        handleClose() {
            this.resetState();
            this.$emit('close');
        },
        resetState() {
            this.status = '';
            this.errorDisplayMessage = '';
            this.uploading = false;
            this.selectedFile = null;
            this.invalidRecordsFile = '';
            this.uploadedFileName = '';
            this.$refs.uploader?.clear();
        },
        onFileSelect(event) {
            this.status = '';
            this.errorDisplayMessage = '';
            this.invalidRecordsFile = '';
            this.uploadedFileName = '';

            const files = event.files || [];
            if (files.length > 1) {
                if (this.$refs.uploader) {
                    this.$refs.uploader.clear();
                    this.$refs.uploader.files = [files[0]];
                }
                this.selectedFile = files[0];
            } else if (files.length === 1) {
                this.selectedFile = files[0];
            } else {
                this.selectedFile = null;
            }
        },
        onFileClear() {
            this.selectedFile = null;
        },
        getErrorDownloadFileName() {
            if (this.uploadedFileName) {
                return this.uploadedFileName;
            }
            return 'rm_upload_invalid_records.xlsx';
        },
        downloadErrorRecords() {
            if (!this.invalidRecordsFile) {
                return;
            }

            try {
                let base64 = this.invalidRecordsFile;
                if (base64.includes(',')) {
                    base64 = base64.split(',')[1];
                }

                const byteCharacters = window.atob(base64);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const link = document.createElement('a');
                const objectUrl = window.URL.createObjectURL(blob);
                link.href = objectUrl;
                link.download = this.getErrorDownloadFileName();
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(objectUrl);
            } catch (error) {
                console.error('Error records download failed:', error);
                this.errorDisplayMessage = 'Failed to download error records';
            }
        },
        async handleUpload(event) {
            const files = event.files || (Array.isArray(event) ? event : []);
            const file = this.selectedFile || (Array.isArray(files) ? files[0] : (files.length > 0 ? files[0] : null));

            if (!file) {
                this.errorDisplayMessage = 'Please select a file first.';
                if (event.options && typeof event.options.clear === 'function') {
                    event.options.clear();
                }
                return;
            }

            if (files.length > 1) {
                this.errorDisplayMessage = 'Only one file is allowed at a time. Please select a single file.';
                if (event.options && typeof event.options.clear === 'function') {
                    event.options.clear();
                }
                this.selectedFile = null;
                return;
            }

            try {
                this.uploading = true;
                this.errorDisplayMessage = '';
                this.status = '';
                this.invalidRecordsFile = '';
                this.uploadedFileName = '';

                const formData = new FormData();
                formData.append('file', file);

                const response = await this.backendApi('fileUpload', formData, {}, 'post');

                if (!response?.data) {
                    this.errorDisplayMessage = 'Failed to upload';
                } else {
                    const responseData = response.data.data || {};
                    const invalidCount = Number(responseData.invalidRecordsCount);
                    const hasInvalidRecordsFile = !!responseData.invalidRecordsFile;
                    const hasInvalidRecords =
                        hasInvalidRecordsFile ||
                        (Number.isFinite(invalidCount) && invalidCount > 0);
                    const hasUploadStatus = !!responseData.uploadStatus;
                    const hasErrors = !!response.data.errors?.length;

                    // Partial validation returns errors[] together with invalidRecordsFile
                    if (hasUploadStatus || hasInvalidRecords) {
                        // Clear file picker first — onFileClear must not wipe status/download state
                        this.selectedFile = null;
                        if (this.$refs.uploader) {
                            this.$refs.uploader.clear();
                        }
                        if (event.options && typeof event.options.clear === 'function') {
                            event.options.clear();
                        }

                        if (hasUploadStatus && !hasInvalidRecords) {
                            this.status = responseData.uploadStatus;
                        }
                        if (hasErrors) {
                            this.errorDisplayMessage = stripHtml(response.data.errors[0].errorDisplayMessage);
                        } else if (hasInvalidRecords && !hasErrors) {
                            this.errorDisplayMessage =
                                `${invalidCount || ''} row(s) failed validation and were not uploaded.`.trim();
                        }
                        if (hasInvalidRecordsFile) {
                            this.invalidRecordsFile = responseData.invalidRecordsFile;
                            this.uploadedFileName =
                                responseData.invalidRecordsFileName || file.name || 'rm_upload_invalid_records.xlsx';
                        }

                        if (hasUploadStatus && !hasInvalidRecords && this.$toast) {
                            const uploadMsg = responseData.uploadStatus || 'File uploaded successfully';
                            const count = responseData.count;
                            const detail =
                                count != null && count !== ''
                                    ? `${uploadMsg} (Count: ${count})`
                                    : uploadMsg;
                            this.$toast.add({
                                severity: 'success',
                                summary: 'Success',
                                detail,
                                life: 3000
                            });
                        }

                        if (!hasInvalidRecords) {
                            setTimeout(() => {
                                this.handleClose();
                            }, 500);
                        }
                    } else if (hasErrors) {
                        this.errorDisplayMessage = stripHtml(response.data.errors[0].errorDisplayMessage);
                    } else {
                        this.errorDisplayMessage = 'Failed to upload';
                    }
                }
            } catch (error) {
                console.error('Upload error:', error);
                this.errorDisplayMessage = 'Failed to upload';
            } finally {
                this.uploading = false;
                if (event.options && typeof event.options.clear === 'function') {
                    event.options.clear();
                }
            }
        }
    }
}
</script>
<style scoped lang="scss">
@import '@/assets/scss/distributorVariables/variable.scss';

.upload-card {
    .upload-card-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 1rem;

        h3 {
            margin: 0;
            font-size: 1.25rem;
        }
    }

    .upload-rules-section {
        margin-bottom: 1rem;
        border: 1px solid #dee2e6;
        border-radius: 8px;
        background: #f8f9fa;
        overflow: hidden;

        .rules-toggle-btn {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0.75rem 1rem;
            background: transparent;
            border: none;
            cursor: pointer;
            font-size: 0.875rem;
            font-weight: 600;
            color: #000;
            transition: background-color 0.2s;

            &:hover {
                background: rgba(0, 0, 0, 0.02);
            }

            i {
                margin-right: 0.5rem;
                transition: transform 0.2s;
            }

            span {
                flex: 1;
                text-align: left;
            }
        }

        .rules-content {
            padding: 1rem;
            border-top: 1px solid #dee2e6;
            max-height: 500px;
            overflow-y: auto;
            background: #fff;
        }

        .rules-section {
            margin-bottom: 1.5rem;

            .rules-title {
                font-size: 1rem;
                font-weight: 600;
                margin: 0 0 1rem 0;
                color: #000;
            }

            .rule-item {
                margin-bottom: 1rem;
                padding-bottom: 1rem;
                border-bottom: 1px solid rgba(0, 0, 0, 0.05);

                &:last-of-type {
                    border-bottom: none;
                    margin-bottom: 0;
                    padding-bottom: 0;
                }

                strong {
                    display: block;
                    font-size: 0.875rem;
                    font-weight: 600;
                    margin-bottom: 0.5rem;
                    color: #000;
                }

                ul {
                    margin: 0.5rem 0 0 0;
                    padding-left: 1.25rem;

                    li {
                        margin-bottom: 0.5rem;
                        font-size: 0.8125rem;
                        line-height: 1.5;
                        color: #000;

                        &:last-child {
                            margin-bottom: 0;
                        }
                    }
                }
            }
        }

        .mandatory-section {
            background: rgba(220, 53, 69, 0.05);
            border: 1px solid rgba(220, 53, 69, 0.2);
            border-radius: 6px;
            padding: 1rem;
            margin-top: 1rem;

            .mandatory-title {
                font-size: 1rem;
                font-weight: 600;
                margin: 0 0 0.75rem 0;
                color: #dc3545;
            }

            .mandatory-intro {
                font-size: 0.8125rem;
                margin: 0 0 0.75rem 0;
                color: #000;
            }

            .mandatory-list {
                margin: 0.75rem 0;
                padding-left: 1.5rem;
                list-style-type: disc;

                li {
                    margin-bottom: 0.5rem;
                    font-size: 0.8125rem;
                    color: #000;

                    &:last-child {
                        margin-bottom: 0;
                    }
                }
            }

            .mandatory-warning {
                font-size: 0.8125rem;
                font-weight: 600;
                margin: 0.75rem 0 0 0;
                color: #dc3545;
                font-style: italic;
            }
        }
    }
}

.upload-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 1.5rem;

    .icon {
        font-size: 2.5rem;
        color: #6c757d;
        margin-bottom: 1rem;
    }

    p {
        margin: 0 0 0.25rem;
        font-weight: 600;
    }

    small {
        color: #6c757d;
    }
}

.upload-status {
    margin-top: 1rem;

    .success-text {
        color: #28a745;
        font-weight: 600;
    }

    .error-text {
        color: #dc3545;
        font-weight: 600;
    }

    .warning-text {
        color: #ffc107;
        font-size: 1rem;
    }

    .download-error-btn {
        margin-top: 0.75rem;
    }
}

:deep(.p-fileupload) {
    border: 1px dashed #dee2e6;
    border-radius: 8px;
    background: #fff;

    .p-fileupload-buttonbar {
        background: transparent;
        border-bottom: 1px solid #dee2e6;
        padding: 1rem;

        .p-button {
            background: #007bff;
            border: 1px solid #007bff;

            &:hover {
                background: #0056b3;
                border-color: #0056b3;
            }
        }

        .p-button.p-button-outlined {
            background: transparent;
            color: #007bff;
            border-color: #007bff;
        }
    }

    .p-fileupload-content {
        padding: 1rem;
        background: #fff;
    }
}
</style>

